<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useMouse, useRafFn, useElementBounding, useMagicKeys, onClickOutside } from '@vueuse/core'
import { PopoverRoot, PopoverAnchor, PopoverContent, PopoverPortal, PopoverArrow } from 'reka-ui'
import { VideoPlayer } from '#components'
import helpVideos from '@/utils/help-videos.js'

// Add color mode detection
const colorMode = useColorMode()
const isDark = computed(() => {
  return colorMode.value === 'dark' || colorMode.preference === 'dark'
})

// Define props
const props = defineProps<{
  visible: boolean
  panelId?: string
  toolUid?: string
  dismissed?: boolean
  sticky?: boolean
  targetElement?: HTMLElement | null
  arrow?: boolean
  followAxis?: 'both' | 'horizontal' | 'vertical' | 'none'
  arrowPosition?: string // e.g., 'top-left', 'bottom-right', 'middle-top', 'middle-left', etc.
  dismissOnDistance?: boolean // Whether to dismiss when exceeding distance threshold
  maxDistanceExceedances?: number // How many times distance can be exceeded before dismissal (0 = unlimited)
  dismissOnTargetClick?: boolean // Whether to dismiss when target element is clicked/pressed (default: false)
  dismissOnClickOutside?: boolean // Whether to dismiss when clicking outside the popover (default: true)
  positionSide?: 'left' | 'right' // Which side of target to position on (default: 'right')
  videoId?: string // Specific video ID to show (e.g., 'mJe0EhXK73Y')
  // Reka UI specific props
  disableUpdateOnLayoutShift?: boolean
  hideWhenDetached?: boolean
  prioritizePosition?: boolean
  // Sequence support
  isSequence?: boolean
  sequenceProgress?: { current: number; total: number; isLast: boolean; isFirst: boolean } | null
  currentStepTitle?: string
  currentStepDescription?: string
}>()

// Use mouse position
const { x: mouseX, y: mouseY } = useMouse()

// Get target element bounds for sticky mode
const targetBounds = computed(() => {
  if (!props.sticky || !props.targetElement) return null
  return useElementBounding(props.targetElement)
})

// Popover element ref
const popoverRef = ref<HTMLElement>()

// Store the reference point when popover becomes visible
const referenceX = ref(0)
const referenceY = ref(0)

// Setup escape key and click outside to dismiss
const { Escape } = useMagicKeys()
watch(Escape, (pressed) => {
  if (pressed && props.visible) {
    emit('dismiss')
  }
})

// Setup click outside to dismiss (only if enabled)
onClickOutside(popoverRef, () => {
  if (props.visible && props.dismissOnClickOutside !== false) { // Default to true
    emit('dismiss')
  }
})

// Setup click/mousedown on target element to dismiss
const handleTargetInteraction = () => {
  if (props.visible) {
    emit('dismiss')
  }
}

// Helper function to get the actual DOM element from a ref or direct element
const getActualElement = (element: any): HTMLElement | null => {
  if (!element) return null
  
  // If it's a Vue ref, get the value
  if (element.value !== undefined) {
    return element.value
  }
  
  // If it's already a DOM element
  if (element.addEventListener) {
    return element
  }
  
  return null
}

// Watch for target element changes to add/remove event listeners
watch(() => props.targetElement, (newTarget, oldTarget) => {
  // Only handle target dismissal if explicitly enabled
  if (!props.dismissOnTargetClick) return
  
  // Remove listeners from old target
  const oldElement = getActualElement(oldTarget)
  if (oldElement) {
    oldElement.removeEventListener('click', handleTargetInteraction)
    oldElement.removeEventListener('mousedown', handleTargetInteraction)
  }
  
  // Add listeners to new target
  const newElement = getActualElement(newTarget)
  if (newElement) {
    newElement.addEventListener('click', handleTargetInteraction)
    newElement.addEventListener('mousedown', handleTargetInteraction)
  }
}, { immediate: true })

// Clean up event listeners on unmount
onUnmounted(() => {
  if (props.dismissOnTargetClick) {
    const element = getActualElement(props.targetElement)
    if (element) {
      element.removeEventListener('click', handleTargetInteraction)
      element.removeEventListener('mousedown', handleTargetInteraction)
    }
  }
})

// Watch for visibility changes to set reference point and reset flags
watch(() => props.visible, (isVisible) => {
  if (isVisible) {
    if (props.sticky && targetBounds.value) {
      // For sticky mode, use element position as reference
      referenceX.value = targetBounds.value.left.value + targetBounds.value.width.value / 2
      referenceY.value = targetBounds.value.top.value + targetBounds.value.height.value / 2
    } else {
      // For cursor mode, use mouse position as reference
      referenceX.value = mouseX.value
      referenceY.value = mouseY.value
    }
    // Reset the distance threshold flags for new hover session
    hasExceeded300px.value = false
    distanceExceedanceCount.value = 0
    isCurrentlyBeyondThreshold.value = false
    // Reset hover state for Reka UI positioned popovers
    hasMouseHoveredOverPopover.value = false
  }
})

// Determine positioning mode: use Reka UI for sticky with target element, custom for everything else
const useRekaPositioning = computed(() => {
  return !!(props.sticky && props.targetElement)
})

// Calculate distance from reference point
const distanceFromReference = computed(() => {
  if (!props.visible) return 0
  
  // For Reka UI positioning with target element, always use target center as reference
  const refX = (useRekaPositioning.value && targetBounds.value) ? 
    targetBounds.value.left.value + targetBounds.value.width.value / 2 : 
    referenceX.value
  const refY = (useRekaPositioning.value && targetBounds.value) ? 
    targetBounds.value.top.value + targetBounds.value.height.value / 2 : 
    referenceY.value
  
  const deltaX = mouseX.value - refX
  const deltaY = mouseY.value - refY
  return Math.sqrt(deltaX * deltaX + deltaY * deltaY)
})

// Easing function: in-out quint (0.86, 0.0, 0.07, 1.0)
const easeInOutQuint = (t: number): number => {
  return t < 0.5 ? 16 * t * t * t * t * t : 1 - Math.pow(-2 * t + 2, 5) / 2
}

// Calculate opacity based on distance (0 at 200px)
const opacity = computed(() => {
  if (!props.visible || props.dismissed) return 0
  
  // For Reka UI positioned popovers, start at full opacity until mouse hovers over it
  if (useRekaPositioning.value && !hasMouseHoveredOverPopover.value) {
    return 1
  }
  
  const distance = distanceFromReference.value
  const maxDistance = 100
  const dismissThreshold = 150
  
  // Check if distance-based dismissal is enabled
  if (props.dismissOnDistance !== false) { // Default to true if not specified
    const maxExceedances = props.maxDistanceExceedances ?? 1 // Default to 1 if not specified
    const beyondThreshold = distance >= dismissThreshold
    
    // Check if user just moved beyond dismissal threshold (entering the zone)
    if (beyondThreshold && !isCurrentlyBeyondThreshold.value && !hasExceeded300px.value) {
      isCurrentlyBeyondThreshold.value = true
      distanceExceedanceCount.value++
      
      console.log(`Distance threshold exceeded ${distanceExceedanceCount.value} times (max: ${maxExceedances === 0 ? 'unlimited' : maxExceedances})`)
      
      // Check if we've reached the limit
      if (maxExceedances > 0 && distanceExceedanceCount.value >= maxExceedances) {
        // Reached the limit, dismiss
        hasExceeded300px.value = true
        emit('dismiss')
        return 0
      }
    }
    
    // Reset the threshold tracking when user moves back within range
    if (!beyondThreshold && isCurrentlyBeyondThreshold.value) {
      isCurrentlyBeyondThreshold.value = false
    }
  }
  
  // If already dismissed by distance rule, stay hidden
  if (hasExceeded300px.value) return 0
  
  // For Reka UI positioned elements, check if distance-based fading is disabled
  if (useRekaPositioning.value && props.dismissOnDistance === false) return 1
  
  if (distance >= maxDistance) return 0
  
  // Calculate linear progress (0 to 1, where 0 = at reference point, 1 = at max distance)
  const linearProgress = distance / maxDistance
  
  // Apply easing curve to create smooth nonlinear fade
  const easedProgress = easeInOutQuint(linearProgress)
  
  // Return inverted eased value (1 at reference point, 0 at max distance)
  return Math.max(0, 1 - easedProgress)
})

// Auto-hide when opacity is 0 for too long
const hideTimeout = ref<NodeJS.Timeout | null>(null)
const emit = defineEmits<{
  hide: []
  dismiss: []
  next: []
  previous: []
  skip: []
}>()

// Track if user moved too far away (for distance-based dismissal)
const hasExceeded300px = ref(false)
const distanceExceedanceCount = ref(0)
const isCurrentlyBeyondThreshold = ref(false)

// Track if mouse has hovered over the popover (for Reka UI positioning)
const hasMouseHoveredOverPopover = ref(false)

watch(opacity, (newOpacity) => {
  if (newOpacity <= 0) {
    // Start hide timer when opacity reaches 0
    if (!hideTimeout.value) {
      hideTimeout.value = setTimeout(() => {
        emit('hide')
        hideTimeout.value = null
      }, 1000) // Hide after 1 second at 0 opacity
    }
  } else {
    // Clear hide timer if opacity goes above 0
    if (hideTimeout.value) {
      clearTimeout(hideTimeout.value)
      hideTimeout.value = null
    }
  }
})

// Clean up timeout on unmount
onUnmounted(() => {
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value)
  }
})



// Get current help video (memoized to prevent VideoPlayer re-renders)
const currentVideo = computed(() => {
  // If a specific video ID is provided, find that video
  if (props.videoId) {
    const video = helpVideos.find(v => v.src_id === props.videoId)
    if (video) return video
  }
  
  // Fall back to first video as default
  return helpVideos[0] // Return the same video object to prevent re-mounting
})

// Conditional event handlers for Reka UI events
const handlePointerDownOutside = () => {
  if (props.dismissOnClickOutside !== false) { // Default to true
    emit('dismiss')
  }
}

const handleFocusOutside = () => {
  if (props.dismissOnClickOutside !== false) { // Default to true
    emit('dismiss')
  }
}

// Handle mouse enter/leave for Reka UI popovers
const handlePopoverMouseEnter = () => {
  if (useRekaPositioning.value) {
    hasMouseHoveredOverPopover.value = true
  }
}

const handlePopoverMouseLeave = () => {
  // Don't reset on mouse leave - once hovered, distance fading should remain active
}

// Calculate position based on mode and follow axis
const calculatePosition = () => {
  if (props.sticky && targetBounds.value) {
    // Position relative to target element
    const bounds = targetBounds.value
    const positionSide = props.positionSide || 'right'
    
    let x = positionSide === 'left' 
      ? bounds.left.value - 320 - 80 // Position to left of element (assuming popover width ~320px)
      : bounds.right.value + 20 // Position to right of element
    
    let y = bounds.top.value + (bounds.height.value / 2) - 50 // Center vertically on element
    
    // Apply axis-specific mouse following for sticky mode
    if (props.followAxis === 'horizontal' || props.followAxis === 'both') {
      x = mouseX.value + 20 // Follow mouse horizontally
    }
    if (props.followAxis === 'vertical' || props.followAxis === 'both') {
      y = mouseY.value - 50 // Follow mouse vertically
    }
    
    return { x, y }
  } else {
    // Follow cursor (original behavior)
    return {
      x: mouseX.value + 20,
      y: mouseY.value - 50
    }
  }
}

// Rounded arrow positioning system
const arrowStyle = computed(() => {
  if (!props.arrow || !props.arrowPosition) {
    return { display: 'none' as const }
  }
  
  const position = props.arrowPosition.toLowerCase()
  const [verticalPos, horizontalPos] = position.split('-')
  
  let style: any = {
    position: 'absolute' as const,
    width: '25px',
    height: '25px',
    zIndex: '20',
    top: '24px',
    borderRadius: '8px 0 0'
  }
  
  // Determine which side the arrow is on and its position along that side
  if (horizontalPos === 'left') {
    // Arrow on left side, pointing right (towards target)
    style.left = '-18px'
    style.transform = 'rotate(135deg)'
    style.background = `linear-gradient(-45deg, hsl(var(--primary)) 50%, transparent 50%)`
    
    // Vertical positioning along left side
    if (verticalPos === 'top') {
      style.top = '30px'
    } else if (verticalPos === 'middle') {
      style.top = '50%'
      style.transform = 'translateY(-50%) rotate(-45deg)'
    } else if (verticalPos === 'bottom') {
      style.bottom = '20px'
    }
  } else if (horizontalPos === 'right') {
    // Arrow on right side, pointing left (towards target)
    style.right = '-18px'
    style.transform = 'rotate(135deg)'
    style.background = `linear-gradient(135deg, hsl(var(--primary)) 50%, transparent 50%)`
    
    // Vertical positioning along right side
    if (verticalPos === 'top') {
      style.top = '50px'
    } else if (verticalPos === 'middle') {
      style.top = '50%'
      style.transform = 'translateY(-50%) rotate(135deg)'
    } else if (verticalPos === 'bottom') {
      style.bottom = '20px'
    }
  } else if (horizontalPos === 'top') {
    // Arrow on top side, pointing down (towards target)
    style.top = '-18px'
    style.transform = 'rotate(-135deg)'
    style.background = `linear-gradient(-135deg, hsl(var(--primary)) 50%, transparent 50%)`
    
    // Horizontal positioning along top side
    if (verticalPos === 'left') {
      style.left = '20px'
    } else if (verticalPos === 'middle') {
      style.left = '50%'
      style.transform = 'translateX(-50%) rotate(-135deg)'
    } else if (verticalPos === 'right') {
      style.right = '20px'
    }
  } else if (horizontalPos === 'bottom') {
    // Arrow on bottom side, pointing up (towards target)
    style.bottom = '-18px'
    style.transform = 'rotate(45deg)'
    style.background = `linear-gradient(45deg, hsl(var(--primary)) 50%, transparent 50%)`
    
    // Horizontal positioning along bottom side
    if (verticalPos === 'left') {
      style.left = '20px'
    } else if (verticalPos === 'middle') {
      style.left = '50%'
      style.transform = 'translateX(-50%) rotate(45deg)'
    } else if (verticalPos === 'right') {
      style.right = '20px'
    }
  }
  
  return style
})

// Close button positioning based on arrow position (opposite side)
const closeButtonStyle = computed(() => {
  if (!props.arrow || !props.arrowPosition) {
    return { top: '8px', right: '8px' } // Default position
  }
  
  const position = props.arrowPosition.toLowerCase()
  const [verticalPos, horizontalPos] = position.split('-')
  
  let style: any = {}
  
  // Position on opposite side from arrow
  if (horizontalPos === 'left') {
    // Arrow on left, close button on right
    style.top = '8px'
    style.right = '8px'
  } else if (horizontalPos === 'right') {
    // Arrow on right, close button on left
    style.top = '8px'
    style.left = '8px'
  } else if (horizontalPos === 'top') {
    // Arrow on top, close button on bottom
    style.bottom = '8px'
    style.right = '8px'
  } else if (horizontalPos === 'bottom') {
    // Arrow on bottom, close button on top
    style.top = '8px'
    style.right = '8px'
  }
  
  return style
})

// Video container border radius based on close button position
const videoContainerClass = computed(() => {
  // Check if close button will be shown
  const showCloseButton = props.sticky || props.followAxis === 'none'
  
  if (!props.arrow || !props.arrowPosition || !showCloseButton) {
    return 'aspect-video rounded-t-xl overflow-hidden border-b-0 border-2 border-primary shadow-xxxl z-10'
  }
  
  const position = props.arrowPosition.toLowerCase()
  const [verticalPos, horizontalPos] = position.split('-')
  
  let roundingClass = 'rounded-t-xl'
  
  // Apply custom rounding to the corner where close button is positioned
  if (horizontalPos === 'left') {
    // Close button on top-right
    roundingClass = '!rounded-tr-[20px] rounded-t-xl'
  } else if (horizontalPos === 'right') {
    // Close button on top-left
    roundingClass = '!rounded-tl-[20px] rounded-t-xl'
  } else if (horizontalPos === 'top') {
    // Close button on bottom-right
    roundingClass = '!rounded-br-[20px] rounded-xl'
  } else if (horizontalPos === 'bottom') {
    // Close button on top-right
    roundingClass = '!rounded-tr-[20px] rounded-t-xl'
  }
  
  return `aspect-video ${roundingClass} overflow-hidden border-b-0 border-2 border-primary shadow-xxxl z-10`
})

// Use CSS custom properties for better performance
const popoverEl = ref<HTMLElement>()
const currentOpacity = ref(1)
const currentPointerEvents = ref<'auto' | 'none'>('auto')

// Update position and opacity with RAF for smooth performance (only for custom positioning)
useRafFn(() => {
  if (useRekaPositioning.value || !popoverEl.value || !props.visible) return
  
  const newOpacity = opacity.value
  const shouldUpdatePointerEvents = newOpacity > 0.1 ? 'auto' : 'none'
  const position = calculatePosition()
  
  // Only update if values changed to avoid unnecessary DOM operations
  if (currentOpacity.value !== newOpacity) {
    popoverEl.value.style.setProperty('--opacity', newOpacity.toString())
    currentOpacity.value = newOpacity
  }
  
  if (currentPointerEvents.value !== shouldUpdatePointerEvents) {
    popoverEl.value.style.setProperty('--pointer-events', shouldUpdatePointerEvents)
    currentPointerEvents.value = shouldUpdatePointerEvents
  }
  
  popoverEl.value.style.setProperty('--mouse-x', `${position.x}px`)
  popoverEl.value.style.setProperty('--mouse-y', `${position.y}px`)
})

// Simple style object for initial positioning
const popoverStyle = {
  position: 'fixed' as const,
  left: 'var(--mouse-x, 0px)',
  top: 'var(--mouse-y, 0px)',
  opacity: 'var(--opacity, 1)',
  pointerEvents: 'var(--pointer-events, auto)' as any,
  zIndex: 40 // Lower z-index to stay below modal dialogs
}
</script>

<template>
  <!-- Reka UI positioning for sticky elements with target -->
  <PopoverRoot v-if="useRekaPositioning" :open="visible && !dismissed" :modal="false">
    <PopoverAnchor v-if="targetElement" :reference="targetElement as any" />
    <PopoverPortal>
      <PopoverContent
        ref="popoverRef"
        :side="positionSide === 'left' ? 'left' : 'right'"
        :align="arrowPosition?.includes('top') ? 'start' : arrowPosition?.includes('bottom') ? 'end' : 'center'"
        :side-offset="0"
        :align-offset="0"
        :avoid-collisions="true"
        :collision-padding="8"
        :sticky="sticky ? 'partial' : undefined"
        :update-position-strategy="'always'"
        :disable-update-on-layout-shift="disableUpdateOnLayoutShift"
        :hide-when-detached="hideWhenDetached"
        :prioritize-position="prioritizePosition"
        :style="{ opacity: opacity }"
        class="bg-transparent border-0 rounded-lg shadow-none p-0 max-w-sm will-change-transform z-[60]"
        @escape-key-down="emit('dismiss')"
        @pointer-down-outside="handlePointerDownOutside"
        @focus-outside="handleFocusOutside"
        @mouseenter="handlePopoverMouseEnter"
        @mouseleave="handlePopoverMouseLeave"
      >
        <!-- Arrow -->
        <PopoverArrow 
          v-if="arrow" 
          class="fill-primary" 
          :width="25" 
          :height="12" 
          :rounded="true"
        />
        <!-- for mouse following help popovers, there should be no close button -->
        <div class="absolute z-10" :style="closeButtonStyle" @click="emit('dismiss')">
          <Button variant="text" class="w-8 h-8 p-0 rounded-full bg-popover border-2 border-primary" >
            <Icon name="material-symbols:close-rounded" class="w-5 h-5" />
          </Button>
        </div>
        
        <div v-motion-pop-visible v-if="currentVideo" class="space-y-0 relative">
          <!-- Video Player -->
          <div :class="videoContainerClass">
            <VideoPlayer
              :src_id="currentVideo.src_id"
              :ratio="currentVideo.ratio"
              :autoplay="true"
              :muted="true"
              :volume="0"
              :controls="[]"
              :loop="{ active: true }"
              :click-to-play="false"
              class="w-full h-full"
            />
          </div>
          
          <!-- Title and Description -->
          <div class="bg-popover/90 backdrop-blur-xl rounded-b-xl p-3 -top-2 relative border-t-0 border-2 border-primary">
            <h3 class="font-semibold text-sm text-foreground mb-1">
              {{ currentStepTitle || currentVideo.title }}
            </h3>
            <p class="text-xs text-muted-foreground leading-relaxed">
              {{ currentStepDescription || currentVideo.description }}
            </p>
            
            <!-- Sequence Navigation -->
            <div v-if="isSequence && sequenceProgress" class="mt-3 pt-2 border-t border-border/20">
              <!-- Navigation buttons -->
              <div class="flex items-center justify-between gap-4">
                <button 
                  v-if="!sequenceProgress.isFirst"
                  variant="text"
                  @click="emit('previous')"
                  :disabled="sequenceProgress.isFirst"
                  :class="[
                    'px-1 py-1 text-xs rounded transition-colors rounded-full aspect-square',
                    sequenceProgress.isFirst 
                      ? 'text-muted-foreground/50 cursor-not-allowed' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  ]"
                >
                  <Icon name="solar:alt-arrow-left-outline" class="w-4 h-4" />
                </button>
                
                <div class="flex-1 bg-muted/60 rounded-full h-1">
                  <div 
                    class="bg-primary h-1 rounded-full transition-all duration-300"
                    :style="{ width: `${(sequenceProgress.current / sequenceProgress.total) * 100}%` }"
                  />
                </div>
                
                <button 
                  @click="sequenceProgress.isLast ? emit('dismiss') : emit('next')"
                  class="px-3 py-1 text-xs rounded transition-colors bg-primary text-primary-foreground rounded-full"
                >
                  {{ sequenceProgress.isLast ? 'Finish' : 'Next' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>

  <!-- Custom positioning for cursor-following (tab hovers) -->
  <Teleport v-else to="body">
    <div 
      v-if="visible"
      ref="popoverEl"
      :style="popoverStyle"
      class="bg-transparent border-0 rounded-lg shadow-none p-0 max-w-sm will-change-transform"
    >
      <!-- Arrow -->
      <div
        v-if="arrow && arrowPosition"
        :style="arrowStyle"
      />
      
      <!-- Close Button -->
      <div v-if="sticky || followAxis === 'none'" class="absolute z-10" :style="closeButtonStyle" @click="emit('dismiss')">
        <Button variant="text" class="w-8 h-8 p-0 rounded-full bg-popover border-2 border-primary" >
          <Icon name="material-symbols:close-rounded" class="w-5 h-5" />
        </Button>
      </div>
      
      <div v-motion-pop-visible v-if="currentVideo" class="space-y-0 relative">
        <!-- Video Player -->
        <div :class="videoContainerClass">
          <VideoPlayer
            :src_id="currentVideo.src_id"
            :ratio="currentVideo.ratio"
            :autoplay="true"
            :muted="true"
            :volume="0"
            :controls="[]"
            :loop="{ active: true }"
            :click-to-play="false"
            class="w-full h-full"
          />
        </div>
        
        <!-- Title and Description -->
        <div class="bg-popover/90 backdrop-blur-xl rounded-b-xl p-3 -top-2 relative border-t-0 border-2 border-primary">
          <h3 class="font-semibold text-sm text-foreground mb-1">
            {{ currentStepTitle || currentVideo.title }}
          </h3>
          <p class="text-xs text-muted-foreground leading-relaxed">
            {{ currentStepDescription || currentVideo.description }}
          </p>
          
          <!-- Sequence Navigation -->
          <div v-if="isSequence && sequenceProgress" class="mt-3 pt-2 border-t border-border/20">
            <!-- Navigation buttons -->
            <div class="flex items-center justify-between gap-4">
              <button 
                v-if="!sequenceProgress.isFirst"
                variant="text"
                @click="emit('previous')"
                :disabled="sequenceProgress.isFirst"
                :class="[
                  'px-1 py-1 text-xs rounded transition-colors rounded-full aspect-square',
                  sequenceProgress.isFirst 
                    ? 'text-muted-foreground/50 cursor-not-allowed' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                ]"
              >
                <Icon name="solar:alt-arrow-left-outline" class="w-4 h-4" />
              </button>
              
              <div class="flex-1 bg-muted/60 rounded-full h-1">
                <div 
                  class="bg-primary h-1 rounded-full transition-all duration-300"
                  :style="{ width: `${(sequenceProgress.current / sequenceProgress.total) * 100}%` }"
                />
              </div>
              
              <button 
                @click="sequenceProgress.isLast ? emit('dismiss') : emit('next')"
                class="px-3 py-1 text-xs rounded transition-colors bg-primary text-primary-foreground rounded-full"
              >
                {{ sequenceProgress.isLast ? 'Finish' : 'Next' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* Optimize rendering performance */
:deep(.plyr) {
  will-change: opacity;
  transform: translateZ(0); /* Force hardware acceleration */
}

/* Ensure smooth opacity transitions */
div[ref="popoverEl"] {
  backface-visibility: hidden;
  transform: translateZ(0);
}
</style> 